import { ControlChartStatisticsApi } from "src/api/generated/spc/controlChart/api/controlChartStatisticsApi.ts";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { AavoECharts } from "src/components/common/echarts/AavoECharts.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import {
	calculateControlChartStatistics,
	calculateStatisticsChartBounds,
	normalDistributionPoints,
} from "./controlChartStatistics.ts";
import { formatNumber } from "src/utils/numberUtils.ts";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import { Dayjs } from "dayjs";
import i18n from "i18next";

export interface ControlChartStatisticsViewProps {
	controlChartId: number;
	sampleSize: number;
	beforeDate?: Dayjs | string | null;
	displayTitle?: string;
}

export function ControlChartStatisticsView({
	controlChartId,
	sampleSize,
	beforeDate,
	displayTitle,
}: ControlChartStatisticsViewProps) {
	return (
		<VerticalBox sx={{ flex: 1, minHeight: 0 }}>
			{displayTitle != null && <Typography variant={"h6"}>{displayTitle}</Typography>}
			<AsyncFetchRender
				fetch={() =>
					ControlChartStatisticsApi.getStatistics({
						controlChartId,
						sampleSize,
						beforeDate: typeof beforeDate === "string" ? beforeDate : (beforeDate?.toISOString() ?? null),
					})
				}
				content={(result) => {
					if (result.source.records.length < 2 || result.withinStandardDeviation == null) {
						return <Typography>{i18n.t("not_enough_data_for_statistics")}</Typography>;
					}
					const statistics = calculateControlChartStatistics({
						values: result.source.records.map((record) => record.primaryValue),
						withinStandardDeviation: result.withinStandardDeviation,
						lsl: result.source.lsl,
						usl: result.source.usl,
						target: result.source.target,
					});
					return (
						<HorizontalBox sx={{ flex: 1, minHeight: 400, alignItems: "stretch" }}>
							<StatisticsSummary
								statistics={statistics}
								lsl={result.source.lsl}
								usl={result.source.usl}
								target={result.source.target}
							/>
							<StatisticsChart
								statistics={statistics}
								lsl={result.source.lsl}
								usl={result.source.usl}
								target={result.source.target}
							/>
						</HorizontalBox>
					);
				}}
			/>
		</VerticalBox>
	);
}

interface SummaryProps {
	statistics: ReturnType<typeof calculateControlChartStatistics>;
	lsl?: number | null;
	usl?: number | null;
	target?: number | null;
}

function StatisticsSummary({ statistics, lsl, usl, target }: SummaryProps) {
	return (
		<VerticalBox sx={{ minWidth: 260, padding: 2, gap: 0.5, overflow: "auto" }}>
			<Statistic label={i18n.t("sample_size")} value={statistics.sampleCount} />
			<Statistic label={i18n.t("mean")} value={statistics.mean} digits={2} />
			<Statistic label={i18n.t("overall_standard_deviation")} value={statistics.overallStandardDeviation} />
			<Statistic label={i18n.t("within_standard_deviation")} value={statistics.withinStandardDeviation} />
			<Statistic label={i18n.t("variance")} value={statistics.variance} />
			<Statistic label={i18n.t("skewness")} value={statistics.skewness} digits={6} />
			<Statistic label={i18n.t("kurtosis")} value={statistics.kurtosis} digits={6} />
			<Divider />
			<Statistic label={i18n.t("minimum")} value={statistics.minimum} />
			<Statistic label={i18n.t("first_quartile")} value={statistics.firstQuartile} />
			<Statistic label={i18n.t("median")} value={statistics.median} />
			<Statistic label={i18n.t("third_quartile")} value={statistics.thirdQuartile} />
			<Statistic label={i18n.t("maximum")} value={statistics.maximum} />
			<Divider />
			<Statistic label={"LSL"} value={lsl} digits={2} />
			<Statistic label={i18n.t("target")} value={target} digits={2} />
			<Statistic label={"USL"} value={usl} digits={2} />
			<Statistic label={"Pp"} value={statistics.pp} digits={2} />
			<Statistic label={"Ppk"} value={statistics.ppk} digits={2} />
			<Statistic label={"Cp"} value={statistics.cp} digits={2} />
			<Statistic label={"Cpk"} value={statistics.cpk} digits={2} />
			<Divider />
			<Typography sx={{ fontWeight: 600 }}>{i18n.t("anderson_darling_normality_test")}</Typography>
			<Statistic label={"A²"} value={statistics.andersonDarling} digits={2} />
			<Statistic label={i18n.t("p_value")} value={statistics.andersonDarlingPValue} />
		</VerticalBox>
	);
}

function Statistic({ label, value, digits = 4 }: { label: string; value: number | null | undefined; digits?: number }) {
	return (
		<Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
			<Typography>{label}</Typography>
			<Typography>
				{value == null || !Number.isFinite(value) ? "-" : formatNumber(value, { maxDigits: digits })}
			</Typography>
		</Box>
	);
}

function StatisticsChart({ statistics, lsl, usl, target }: SummaryProps) {
	const { palette } = useTheme();
	const histogram = statistics.histogram;
	const histogramArea = histogram.reduce((sum, [, frequency]) => sum + statistics.histogramBinWidth * frequency, 0);
	const { xMinimum, xMaximum } = calculateStatisticsChartBounds(histogram, statistics.histogramBinWidth, lsl, usl);
	const specificationLines = [
		{ name: "LSL", value: lsl, color: palette.error.main },
		{ name: i18n.t("target"), value: target, color: palette.success.main },
		{ name: "USL", value: usl, color: palette.error.main },
	].filter((item): item is { name: string; value: number; color: string } => item.value != null);
	const sigmaLines = [-3, -2, -1, 0, 1, 2, 3].map((ordinal) => ({
		value: statistics.mean + ordinal * statistics.withinStandardDeviation,
		name: ordinal === 0 ? "μ" : `${ordinal > 0 ? "+" : ""}${ordinal}σ`,
		width: ordinal === 0 ? 1.5 : 1,
	}));
	const sigmaAreas = [1, 2, 3].map((ordinal) => [
		{ xAxis: statistics.mean - ordinal * statistics.withinStandardDeviation },
		{ xAxis: statistics.mean + ordinal * statistics.withinStandardDeviation },
	]);
	return (
		<AavoECharts
			sx={{ flex: 1, minWidth: 0 }}
			option={{
				animation: false,
				legend: { show: false },
				grid: { containLabel: true, left: 20, right: 20, top: 50, bottom: 20 },
				xAxis: { type: "value", min: xMinimum, max: xMaximum },
				yAxis: { show: false },
				tooltip: { trigger: "axis" },
				series: [
					{
						type: "bar",
						data: histogram,
						barWidth: "95%",
						itemStyle: { color: palette.info.main },
						markLine: {
							symbol: "none",
							data: [
								...sigmaLines.map((line) => ({
									xAxis: line.value,
									name: line.name,
									label: { formatter: line.name },
									lineStyle: { color: palette.grey[700], type: "dashed", width: line.width },
								})),
								...specificationLines.map((line) => ({
									xAxis: line.value,
									name: line.name,
									label: { formatter: `${line.name}: ${formatNumber(line.value)}` },
									lineStyle: { color: line.color, width: 2 },
								})),
							],
						},
						markArea: {
							silent: true,
							itemStyle: { color: "rgba(184, 210, 236, 0.1)" },
							data: sigmaAreas,
						},
					},
					{
						type: "line",
						showSymbol: false,
						lineStyle: { color: palette.error.main },
						data: normalDistributionPoints(
							statistics.mean,
							statistics.overallStandardDeviation,
							xMinimum,
							xMaximum,
							histogramArea,
						),
					},
					{
						type: "line",
						showSymbol: false,
						lineStyle: { color: palette.grey[800], type: "dashed" },
						data: normalDistributionPoints(
							statistics.mean,
							statistics.withinStandardDeviation,
							xMinimum,
							xMaximum,
							histogramArea,
						),
					},
				],
			}}
		/>
	);
}
